import { useCallback, useMemo, useState } from 'react';

import { errorMessage } from '../services/apiError';
import { authService } from '../services/authService';
import { oauthService } from '../services/oauthService';
import { redirectAfterAuth, savePostAuthRedirect } from '../services/postAuthRedirect';
import { queueSignupCreditAward } from '../services/signupCreditAward';
import { trackOAuthAuthOutcome } from '../utils/analytics';

interface ModalState {
  isOpen: boolean;
  isLoading: boolean;
  error: string | undefined;
  email: string;
  setIsOpen: (v: boolean) => void;
  setIsLoading: (v: boolean) => void;
  setError: (v: string | undefined) => void;
  setEmail: (v: string) => void;
}

const useModalState = (): ModalState => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [email, setEmail] = useState('');

  return useMemo(() => ({
    isOpen, isLoading, error, email,
    setIsOpen, setIsLoading, setError, setEmail,
  }), [isOpen, isLoading, error, email]);
};

const extractErrorMessage = (err: unknown): string => errorMessage(
  err,
  'We couldn’t complete authentication. Check your details and try again.',
);

const resetModalState = (
  setEmail: (e: string) => void,
  setIsLoading: (l: boolean) => void,
  setError: (e: string | undefined) => void
) => {
  setEmail('');

  setIsLoading(false);

  setError(undefined);
};

const performEmailLogin = async (
  email: string,
  password: string,
  setIsOpen: (o: boolean) => void
) => {
  savePostAuthRedirect();

  await authService.emailLogin(email, password);

  setIsOpen(false);

  redirectAfterAuth();
};

const wrapAsyncHandler = async (fn: () => Promise<void>, onError: (err: unknown) => void) => {
  try {
    await fn();
  } catch (err) {
    onError(err);
  }
};

const initAsyncState = (
  setLoading: (l: boolean) => void,
  setErr: (e: string | undefined) => void
): void => {
  setLoading(true);
  setErr(undefined);
};

const wrapAsyncWithLoading = async (
  fn: () => Promise<void>,
  setLoading: (l: boolean) => void,
  setErr: (e: string | undefined) => void,
  onError: (err: unknown) => void
) => {
  initAsyncState(setLoading, setErr);
  try {
    await fn();
  } catch (err) {
    onError(err);
  } finally {
    setLoading(false);
  }
};

interface NativeAppleCredential {
  identityToken: string;
  authorizationCode?: string;
  user?: string;
}

interface NativeAppleMessage extends NativeAppleCredential {
  type?: string;
  message?: string;
}

interface NativeAuthWindow {
  __IS_IOS_APP__?: boolean;
  ReactNativeWebView?: { postMessage: (message: string) => void };
  __appleNativeSignInCallback__?: (raw: unknown) => void;
}

const getNativeAuthWindow = (): NativeAuthWindow => window as unknown as NativeAuthWindow;

const parseNativeAppleMessage = (raw: unknown): NativeAppleMessage => (
  typeof raw === 'string' ? JSON.parse(raw) as NativeAppleMessage : raw as NativeAppleMessage
);

const requestNativeAppleCredential = (): Promise<NativeAppleCredential> => new Promise((resolve, reject) => {
  const nativeWindow = getNativeAuthWindow();

  nativeWindow.__appleNativeSignInCallback__ = (raw: unknown) => {
    delete nativeWindow.__appleNativeSignInCallback__;

    const data = parseNativeAppleMessage(raw);

    if (data.type === 'APPLE_NATIVE_SIGN_IN_ERROR') {
      reject(new Error(data.message || 'Apple Sign-In failed'));

      return;
    }

    resolve(data);
  };
  nativeWindow.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'APPLE_NATIVE_SIGN_IN' }));
});

const completeNativeAppleLogin = async (): Promise<void> => {
  const credential = await requestNativeAppleCredential();
  const result = await authService.appleNativeCallback(
    credential.identityToken,
    credential.authorizationCode,
    credential.user,
  );

  queueSignupCreditAward(result.signup_credits_awarded);
  trackOAuthAuthOutcome('apple', result.is_new_user === true, result.signup_event_id);
  redirectAfterAuth();
};

const isNativeAppleApp = (): boolean => {
  const nativeWindow = getNativeAuthWindow();

  return !!nativeWindow.__IS_IOS_APP__ && !!nativeWindow.ReactNativeWebView;
};

const useOAuthHandlers = (handleError: (err: unknown) => void) => {
  const handleGoogleLogin = useCallback(
    async () => wrapAsyncHandler(async () => {
      savePostAuthRedirect();
      window.location.href = await oauthService.getGoogleAuthUrl();
    }, handleError),
    [handleError]
  );

  const handleAppleLogin = useCallback(
    async () => wrapAsyncHandler(async () => {
      savePostAuthRedirect();
      if (isNativeAppleApp()) {
        await completeNativeAppleLogin();

        return;
      }

      window.location.href = oauthService.getAppleAuthUrl();
    }, handleError),
    [handleError]
  );

  const handleMicrosoftLogin = useCallback(
    async () => wrapAsyncHandler(async () => {
      savePostAuthRedirect();
      window.location.href = oauthService.getMicrosoftAuthUrl();
    }, handleError),
    [handleError]
  );

  const handleGitHubLogin = useCallback(
    async () => wrapAsyncHandler(async () => {
      savePostAuthRedirect();
      window.location.href = await oauthService.getGitHubAuthUrl();
    }, handleError),
    [handleError]
  );

  return { handleGoogleLogin, handleAppleLogin, handleMicrosoftLogin, handleGitHubLogin };
};

const useEmailHandler = (
  setIsOpen: (o: boolean) => void,
  setIsLoading: (l: boolean) => void,
  setError: (e: string | undefined) => void,
  handleError: (err: unknown) => void
) => useCallback(
  async (email: string, password: string) => wrapAsyncWithLoading(
    () => performEmailLogin(email, password, setIsOpen),
    setIsLoading,
    setError,
    handleError
  ),
  [setIsOpen, setIsLoading, setError, handleError]
);

const useModalActions = (state: ModalState) => {
  // depend on the raw useState setters (stable identities), not the state
  // object rebuilt on every value change — otherwise effects that depend on
  // openModal (e.g. Navbar's auth-URL effect) re-fire on every keystroke or
  // open/close and can reopen the modal right after it was closed
  const { setIsOpen, setIsLoading, setError, setEmail } = state;

  const handleError = useCallback((err: unknown) => {
    setError(extractErrorMessage(err));
  }, [setError]);

  const openModal = useCallback(() => {
    setIsOpen(true);
    resetModalState(setEmail, setIsLoading, setError);
  }, [setIsOpen, setEmail, setIsLoading, setError]);

  const closeModal = useCallback(() => setIsOpen(false), [setIsOpen]);

  return { handleError, openModal, closeModal };
};

const useAllHandlers = (state: ModalState, handleError: (err: unknown) => void) => ({
  oauthHandlers: useOAuthHandlers(handleError),
  handleEmailLogin: useEmailHandler(state.setIsOpen, state.setIsLoading, state.setError, handleError),
});

export const useAuthModal = () => {
  const state = useModalState();
  const { handleError, openModal, closeModal } = useModalActions(state);
  const { oauthHandlers, handleEmailLogin } = useAllHandlers(state, handleError);


  return {
    isOpen: state.isOpen, openModal, closeModal, isLoading: state.isLoading,
    error: state.error, setError: state.setError, email: state.email, setEmail: state.setEmail,
    ...oauthHandlers, handleEmailLogin,
  };
};
